<template>
  <div>
    <slot name="label" :failed="failed">
      <p
        v-if="label"
        class="font-bold"
        :class="{
          'text-error': failed,
          'text-sm': size === 'sm',
          'text-base': size !== 'sm',
        }"
      >
        {{ label }}
      </p>
      <input :value="value" class="hidden" />
    </slot>
    <button
      ref="dropdownToggle"
      class="flex p-2 relative text-left w-full rounded bg-white border border-solid border-gray-300 hover:border-gray-400 focus-within:!border-primary focus-within:shadow-md dark:bg-gray-700 dark:border-gray-600 dark:hover:border-gray-500 dark:text-gray-200"
      type="button"
      :class="{
        '!bg-gray-100 dark:!bg-gray-600': _disabled,
        'cursor-default': _disabled,
        '!p-1 !pl-2 text-sm': size === 'sm',
      }"
      @click="dropdown"
    >
      <IconComponent
        :icon="icon"
        pack="iconPack"
        class="absolute top-1/2 -translate-y-1/2 left-0 ml-2 text-gray-300 placeholder-gray-300 w-6 dark:text-gray-500"
      />
      <span
        class="box-border w-full pr-9 truncate"
        :class="{
          '!pl-9': icon,
        }"
      >
        {{ shownValue }}
      </span>
      <IconComponent
        v-if="!clearable || !value || _disabled"
        icon="angle-down"
        class="absolute top-1/2 -translate-y-1/2 right-0 mr-2 text-gray-300 placeholder-gray-300 w-6 dark:text-gray-500"
      />
      <span
        v-else
        class="absolute top-1/2 -translate-y-1/2 right-0 mr-2 w-6 flex items-center justify-center cursor-pointer"
        @click.stop.prevent="clear"
      >
        <IconComponent
          icon="times"
          class="text-gray-300 placeholder-gray-300 w-6 dark:text-gray-500"
        />
      </span>
    </button>
    <div
      v-show="open"
      ref="dropdownMenu"
      class="bg-white rounded-md z-50 border border-gray-300 shadow-md dark:bg-gray-800 dark:border-gray-600"
    >
      <div ref="container" class="max-h-52 overflow-y-auto py-2">
        <div
          v-for="item in items"
          :key="item[valueKey]"
          :ref="item[valueKey]"
          class="max-md:py-2 md:py-1 pl-3 pr-2 cursor-pointer hover:bg-gray-100 text-sm text-black dark:hover:bg-gray-700 dark:text-gray-100"
          @click.stop.prevent="selectItem(item)"
        >
          {{ item[labelKey] }}
        </div>
      </div>
    </div>
    <p v-show="failed" class="text-sm text-error mt-1">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import { createPopper } from "@popperjs/core";
import { inject } from "@nuxtjs/composition-api";
import IconComponent from "~/components/IconComponent.vue";

const sameWidth = {
  name: "sameWidth",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
  },
};

export default {
  components: { IconComponent },
  props: {
    value: {
      type: [Object, String, Number, Boolean],
      required: false,
      default: null,
    },
    items: {
      type: Array,
      required: true,
    },
    labelKey: {
      type: String,
      default: "label",
    },
    icon: {
      type: String,
      default: "",
    },
    iconPack: {
      type: String,
      default: "fas",
    },
    valueKey: {
      type: String,
      default: "value",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "-",
    },
    label: {
      type: String,
      default: "",
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    validationState: {
      type: Object,
      required: false,
      default: () => {},
    },
    size: {
      type: String,
      default: "md",
      validator: function (val) {
        return ["sm", "md"].includes(val);
      },
    },
  },
  setup() {
    const { disabled: formDisabled } = inject("form", { disabled: false });
    return {
      formDisabled,
    };
  },
  data() {
    return {
      open: false,
      popper: null,
    };
  },
  computed: {
    _disabled() {
      return this.disabled || this.formDisabled;
    },
    selectedItem() {
      return this.items.find((item) => item[this.valueKey] === this.value);
    },
    shownValue() {
      if (this.selectedItem) {
        return this.selectedItem[this.labelKey];
      }
      return this.placeholder;
    },
    errorMessage() {
      return this.validationState?.$errors?.[0]?.$message;
    },
    failed() {
      return this.validationState?.$error;
    },
  },
  mounted() {
    document.addEventListener("click", this.closeOnOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeOnOutsideClick);
    if (this.popper) {
      this.popper.destroy();
    }
  },
  methods: {
    lazyInitPopper() {
      if (!this.popper) {
        this.popper = createPopper(
          this.$refs.dropdownToggle,
          this.$refs.dropdownMenu,
          {
            placement: "bottom-start",
            modifiers: [sameWidth],
          }
        );
      }
    },
    openDropdown() {
      this.open = true;
      this.setPopperEventListeners(true);
    },
    hideDropdown() {
      this.open = false;
      this.setPopperEventListeners(false);
    },
    setPopperEventListeners(enabled) {
      this.lazyInitPopper();
      this.popper.setOptions((options) => ({
        ...options,
        modifiers: [...options.modifiers, { name: "eventListeners", enabled }],
      }));
    },
    selectItem(item) {
      this.hideDropdown();
      this.$emit("input", item[this.valueKey]);
      this.$emit("select", item);
    },
    dropdown() {
      if (this._disabled) return;
      if (this.open) {
        this.hideDropdown();
      } else {
        this.openDropdown();
      }
    },
    closeOnOutsideClick(e) {
      if (!this.$el.contains(e.target)) {
        this.hideDropdown();
      }
    },
    clear() {
      if (this._disabled) return;
      this.$emit("input", null);
    },
  },
};
</script>
